import React, { useEffect, useState } from 'react';
import { MapPin, Store, Utensils, CreditCard, Banknote, Smartphone, Loader2, CheckCircle2, Navigation, AlertCircle } from 'lucide-react';
import { Input, Textarea } from '../ui/Input';
import { Button } from '../ui/Button';
import { DeliveryType, Restaurant, RestaurantSettings } from '../../lib/types';
import { cn, validateCaprichoDeliveryZone } from '../../lib/utils';
import { useTableOrder } from '../../lib/table-context';
import { isPreorderRestaurant, DELIVERY_TIME_SLOTS, getUpcomingSaturdays, formatDeliveryDate, toDateString, getOrderingWindowMessage } from '../../lib/preorder';
import { useCustomerLookup } from '../../lib/loyalty-hooks';
import { LoyaltyCard } from './LoyaltyCard';
interface CheckoutFormProps {
  restaurant: Restaurant | null;
  settings: RestaurantSettings | null;
  onSubmit: (data: CheckoutFormData) => void;
  isLoading: boolean;
  initialCustomer?: {
    customer_name: string;
    customer_phone: string;
    delivery_address: string;
    maps_url: string;
  } | null;
}
export interface CheckoutFormData {
  customer_name: string;
  customer_phone: string;
  delivery_type: DeliveryType;
  payment_method: string;
  delivery_address: string;
  maps_url: string;
  delivery_lat: number | null;
  delivery_lng: number | null;
  table_id: string;
  notes: string;
  preorder_date?: string;
  preorder_time_slot?: string;
}
export function CheckoutForm({
  restaurant,
  settings,
  onSubmit,
  isLoading,
  initialCustomer
}: CheckoutFormProps) {
  const {
    tableOrder,
    isTableOrder
  } = useTableOrder();
  const [formData, setFormData] = useState<CheckoutFormData>({
    customer_name: '',
    customer_phone: '',
    delivery_type: isTableOrder ? 'dine_in' : 'pickup',
    payment_method: 'sinpe',
    delivery_address: '',
    maps_url: '',
    delivery_lat: null,
    delivery_lng: null,
    table_id: isTableOrder ? tableOrder?.tableId || '' : '',
    notes: ''
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof CheckoutFormData, string>>>({});
  const [geoStatus, setGeoStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [geoError, setGeoError] = useState('');
  // Apply initialCustomer data from localStorage lookup
  useEffect(() => {
    if (initialCustomer) {
      setFormData(prev => ({
        ...prev,
        customer_name: initialCustomer.customer_name || prev.customer_name,
        customer_phone: initialCustomer.customer_phone || prev.customer_phone,
        delivery_address: initialCustomer.delivery_address || prev.delivery_address,
        maps_url: initialCustomer.maps_url || prev.maps_url
      }));
    }
  }, [initialCustomer]);
  const isPreorder = isPreorderRestaurant(restaurant?.id);
  const preorderSaturdays = isPreorder ? getUpcomingSaturdays(3) : [];
  const preorderWindow = isPreorder ? getOrderingWindowMessage() : null;
  // Loyalty Integration
  const {
    customer,
    loading: customerLoading
  } = useCustomerLookup(formData.customer_phone, restaurant?.id);
  // Autofill when customer is found
  useEffect(() => {
    if (customer && !formData.customer_name) {
      setFormData(prev => ({
        ...prev,
        customer_name: customer.full_name || prev.customer_name
      }));
    }
  }, [customer]);
  // Autofill address when found
  useEffect(() => {
    if (customer?.address_text && !formData.delivery_address && formData.delivery_type === 'delivery') {
      setFormData(prev => ({
        ...prev,
        delivery_address: customer.address_text || prev.delivery_address,
        maps_url: customer.maps_url || prev.maps_url
      }));
    }
  }, [customer, formData.delivery_type]);
  // Initialize default delivery type based on settings
  useEffect(() => {
    if (settings) {
      if (settings.accept_delivery) setFormData(prev => ({
        ...prev,
        delivery_type: 'delivery'
      }));else if (settings.accept_pickup) setFormData(prev => ({
        ...prev,
        delivery_type: 'pickup'
      }));
    }
  }, [settings]);
  const validate = () => {
    const newErrors: Partial<Record<keyof CheckoutFormData, string>> = {};
    if (!formData.customer_name.trim()) newErrors.customer_name = 'El nombre es requerido';
    // Phone is optional for QR table orders, required otherwise
    if (!isTableOrder) {
      if (!formData.customer_phone.trim()) newErrors.customer_phone = 'El teléfono es requerido';else if (formData.customer_phone.replace(/\D/g, '').length < 8) {
        newErrors.customer_phone = 'Ingresa un número válido de 8 dígitos';
      }
    } else if (formData.customer_phone.trim() && formData.customer_phone.replace(/\D/g, '').length < 8) {
      newErrors.customer_phone = 'Ingresa un número válido de 8 dígitos';
    }
    if (formData.delivery_type === 'delivery') {
      if (!formData.delivery_address.trim()) newErrors.delivery_address = 'La dirección es requerida para delivery';
      // Capricho de Canela: validate delivery zone (5km radius)
      const isCapricho = String(restaurant?.id) === '2';
      if (isCapricho && formData.delivery_lat !== null && formData.delivery_lng !== null) {
        const zoneCheck = validateCaprichoDeliveryZone(formData.delivery_lat, formData.delivery_lng);
        if (!zoneCheck.valid) {
          newErrors.delivery_address = `Lo sentimos, solo hacemos delivery en San Marcos de Tarrazú, Santa María de Dota y San Pablo de León Cortés (máximo 5km del centro). Tu ubicación está a ${zoneCheck.distance?.toFixed(1)}km de ${zoneCheck.nearestZone}.`;
        }
      }
    }
    if (formData.delivery_type === 'dine_in' && !isTableOrder) {
      if (!formData.table_id.trim()) newErrors.table_id = 'Indica tu número de mesa';
    }
    if (isPreorder) {
      if (!formData.preorder_date) (newErrors as any).preorder_date = 'Selecciona una fecha de entrega';
      if (!formData.preorder_time_slot) (newErrors as any).preorder_time_slot = 'Selecciona un horario de entrega';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };
  const handleChange = (field: keyof CheckoutFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };
  const handleGeolocation = () => {
    if (!navigator.geolocation) {
      setGeoStatus('error');
      setGeoError('Tu navegador no soporta geolocalización');
      return;
    }
    setGeoStatus('loading');
    setGeoError('');
    navigator.geolocation.getCurrentPosition(position => {
      const {
        latitude,
        longitude
      } = position.coords;
      // Capricho de Canela: validate delivery zone immediately
      const isCapricho = String(restaurant?.id) === '2';
      if (isCapricho) {
        const zoneCheck = validateCaprichoDeliveryZone(latitude, longitude);
        if (!zoneCheck.valid) {
          setGeoStatus('error');
          setGeoError(`Tu ubicación está fuera de nuestra zona de delivery. Solo entregamos en San Marcos de Tarrazú, Santa María de Dota y San Pablo de León Cortés (máximo 5km del centro). Estás a ${zoneCheck.distance?.toFixed(1)}km de ${zoneCheck.nearestZone}.`);
          return;
        }
      }
      setFormData(prev => ({
        ...prev,
        delivery_lat: latitude,
        delivery_lng: longitude,
        maps_url: `https://maps.google.com/?q=${latitude},${longitude}`
      }));
      setGeoStatus('success');
    }, error => {
      setGeoStatus('error');
      switch (error.code) {
        case error.PERMISSION_DENIED:
          setGeoError('Permiso denegado. Por favor, permite el acceso a tu ubicación.');
          break;
        case error.POSITION_UNAVAILABLE:
          setGeoError('La información de ubicación no está disponible.');
          break;
        case error.TIMEOUT:
          setGeoError('Se agotó el tiempo de espera para obtener la ubicación.');
          break;
        default:
          setGeoError('Ocurrió un error desconocido al obtener la ubicación.');
          break;
      }
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };
  // Determine available payment methods from restaurant
  const showSinpe = restaurant?.accepts_sinpe ?? true;
  const showCash = restaurant?.accepts_cash ?? true;
  const showCard = restaurant?.accepts_card ?? true;
  return <form onSubmit={handleSubmit} className="space-y-8" data-id="element-724">
      {/* QR Table Banner */}
      {isTableOrder && tableOrder && <section className="bg-emerald-50 p-5 rounded-2xl border border-emerald-200 flex items-center gap-4" data-id="element-725">
          <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0" data-id="element-726">
            <Utensils className="w-6 h-6 text-emerald-600" data-id="element-727" />
          </div>
          <div data-id="element-728">
            <p className="font-bold text-emerald-900 text-lg" data-id="element-729">
              {tableOrder.tableName} · Autoatención
            </p>
            <p className="text-sm text-emerald-700" data-id="element-730">
              Tu pedido se enviará directamente a esta mesa
            </p>
          </div>
        </section>}

      {/* Contact Info */}
      <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-731">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-732">Tus Datos</h3>
        <div className="space-y-4" data-id="element-733">
          <Input label="Teléfono (WhatsApp)" type="tel" placeholder="88888888" value={formData.customer_phone} onChange={e => setFormData({
          ...formData,
          customer_phone: e.target.value
        })} error={errors.customer_phone} required data-id="element-734" />
          <LoyaltyCard phone={formData.customer_phone} restaurantId={restaurant?.id} data-id="element-735" />

          <Input label="Nombre completo" type="text" placeholder="Juan Pérez" value={formData.customer_name} onChange={e => handleChange('customer_name', e.target.value)} error={errors.customer_name} required data-id="element-736" />
        </div>
      </section>

      {/* Preorder Schedule — for preorder restaurants */}
      {isPreorder && preorderWindow && <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-737">
          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-738">
            📦 Entrega Programada
          </h3>

          {/* Schedule Info */}
          <div className={cn('rounded-xl p-4 border mb-5', preorderWindow.isOpen ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200')} data-id="element-739">
            <p className={cn('font-bold text-sm mb-1', preorderWindow.isOpen ? 'text-emerald-900' : 'text-red-900')} data-id="element-740">
              {preorderWindow.title}
            </p>
            <p className={cn('text-xs', preorderWindow.isOpen ? 'text-emerald-700' : 'text-red-700')} data-id="element-741">
              {preorderWindow.message}
            </p>
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500" data-id="element-742">
              <span data-id="element-743">📅 Pedidos: Lun–Vie</span>
              <span data-id="element-744">·</span>
              <span data-id="element-745">🚚 Entregas: Sábado 8am–4pm</span>
            </div>
          </div>

          {/* Date Selection */}
          <div className="mb-5" data-id="element-746">
            <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-747">
              Fecha de entrega (sábado)
            </label>
            <div className="space-y-2" data-id="element-748">
              {preorderSaturdays.map(sat => {
            const dateStr = toDateString(sat);
            const isSelected = formData.preorder_date === dateStr;
            return <button key={dateStr} type="button" onClick={() => handleChange('preorder_date' as any, dateStr)} className={cn('w-full p-3.5 rounded-xl border-2 text-sm font-semibold transition-all text-left', isSelected ? 'border-brand-amber bg-amber-50/50 text-gray-900' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-749">
                    📅 {formatDeliveryDate(sat)}
                  </button>;
          })}
            </div>
            {(errors as any).preorder_date && <p className="text-sm text-red-500 mt-1" data-id="element-750">
                {(errors as any).preorder_date}
              </p>}
          </div>

          {/* Time Slot Selection */}
          {formData.preorder_date && <div data-id="element-751">
              <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-752">
                Horario de entrega
              </label>
              <div className="grid grid-cols-2 gap-2" data-id="element-753">
                {DELIVERY_TIME_SLOTS.map(slot => {
            const isSelected = formData.preorder_time_slot === slot.id;
            return <button key={slot.id} type="button" onClick={() => handleChange('preorder_time_slot' as any, slot.id)} className={cn('p-3 rounded-xl border-2 text-xs font-semibold transition-all', isSelected ? 'border-brand-amber bg-amber-50/50 text-gray-900' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-754">
                      🕐 {slot.label}
                    </button>;
          })}
              </div>
              {(errors as any).preorder_time_slot && <p className="text-sm text-red-500 mt-1" data-id="element-755">
                  {(errors as any).preorder_time_slot}
                </p>}
            </div>}
        </section>}

      {/* Delivery Type — hidden for QR table orders */}
      {!isTableOrder && <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-756">
          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-757">
            {isPreorder ? 'Método de Recogida' : 'Método de Entrega'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-758">
            {settings?.accept_delivery && <label className={cn('flex flex-col items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.delivery_type === 'delivery' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-759">
                <input type="radio" name="delivery_type" value="delivery" checked={formData.delivery_type === 'delivery'} onChange={() => handleChange('delivery_type', 'delivery')} className="sr-only" data-id="element-760" />
                <MapPin className="w-6 h-6 mb-2" data-id="element-761" />
                <span className="font-semibold" data-id="element-762">Delivery</span>
              </label>}

            {settings?.accept_pickup && <label className={cn('flex flex-col items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.delivery_type === 'pickup' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-763">
                <input type="radio" name="delivery_type" value="pickup" checked={formData.delivery_type === 'pickup'} onChange={() => handleChange('delivery_type', 'pickup')} className="sr-only" data-id="element-764" />
                <Store className="w-6 h-6 mb-2" data-id="element-765" />
                <span className="font-semibold" data-id="element-766">Para llevar</span>
              </label>}

            {!isPreorder && <label className={cn('flex flex-col items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.delivery_type === 'dine_in' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-767">
                <input type="radio" name="delivery_type" value="dine_in" checked={formData.delivery_type === 'dine_in'} onChange={() => handleChange('delivery_type', 'dine_in')} className="sr-only" data-id="element-768" />
                <Utensils className="w-6 h-6 mb-2" data-id="element-769" />
                <span className="font-semibold" data-id="element-770">Comer aquí</span>
              </label>}
          </div>

          {/* Conditional Fields based on Delivery Type */}
          <div className="mt-6 space-y-4" data-id="element-771">
            {formData.delivery_type === 'delivery' && <>
                <Textarea label="Dirección exacta" placeholder="Provincia, Cantón, Distrito, señas exactas..." value={formData.delivery_address} onChange={e => handleChange('delivery_address', e.target.value)} error={errors.delivery_address} required data-id="element-772" />

                <div className="space-y-2" data-id="element-773">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-774">
                    Ubicación GPS (Opcional pero recomendado)
                  </label>

                  <button type="button" onClick={handleGeolocation} disabled={geoStatus === 'loading'} className={cn('w-full h-12 flex items-center justify-center rounded-xl border-2 border-dashed font-medium transition-all disabled:opacity-70', geoStatus === 'idle' && 'border-gray-200 text-gray-600 hover:border-brand-amber hover:text-brand-amber bg-gray-50', geoStatus === 'loading' && 'border-brand-amber text-brand-amber bg-amber-50/50', geoStatus === 'success' && 'border-emerald-300 text-emerald-700 bg-emerald-50', geoStatus === 'error' && 'border-red-300 text-red-600 bg-red-50')} data-id="element-775">
                    {geoStatus === 'idle' && <>
                        <Navigation className="w-5 h-5 mr-2" data-id="element-776" />
                        Usar mi ubicación actual
                      </>}
                    {geoStatus === 'loading' && <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-777" />
                        Obteniendo ubicación...
                      </>}
                    {geoStatus === 'success' && <>
                        <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-778" />
                        Ubicación capturada
                      </>}
                    {geoStatus === 'error' && <>
                        <AlertCircle className="w-5 h-5 mr-2" data-id="element-779" />
                        Reintentar ubicación
                      </>}
                  </button>

                  {geoStatus === 'success' && <div className="flex items-center justify-between px-1" data-id="element-780">
                      <a href={formData.maps_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 hover:underline flex items-center" data-id="element-781">
                        📍 Ver en el mapa
                      </a>
                      <button type="button" onClick={handleGeolocation} className="text-xs text-gray-500 hover:text-gray-900 underline" data-id="element-782">
                        Actualizar
                      </button>
                    </div>}

                  {geoStatus === 'error' && <p className="text-xs text-red-500 px-1" data-id="element-783">{geoError}</p>}
                </div>
              </>}

            {formData.delivery_type === 'dine_in' && <Input label="Número o nombre de mesa" placeholder="Ej. Mesa 4, Barra, etc." value={formData.table_id} onChange={e => handleChange('table_id', e.target.value)} error={errors.table_id} required data-id="element-784" />}
          </div>
        </section>}

      {/* Payment Method */}
      <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-785">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-786">Método de Pago</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-787">
          {showSinpe && <label className={cn('flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.payment_method === 'sinpe' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-788">
              <input type="radio" name="payment_method" value="sinpe" checked={formData.payment_method === 'sinpe'} onChange={() => handleChange('payment_method', 'sinpe')} className="sr-only" data-id="element-789" />
              <Smartphone className="w-5 h-5 mr-3" data-id="element-790" />
              <span className="font-semibold" data-id="element-791">SINPE Móvil</span>
            </label>}

          {showCash && <label className={cn('flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.payment_method === 'cash' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-792">
              <input type="radio" name="payment_method" value="cash" checked={formData.payment_method === 'cash'} onChange={() => handleChange('payment_method', 'cash')} className="sr-only" data-id="element-793" />
              <Banknote className="w-5 h-5 mr-3" data-id="element-794" />
              <span className="font-semibold" data-id="element-795">Efectivo</span>
            </label>}

          {showCard && <label className={cn('flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.payment_method === 'card' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-796">
              <input type="radio" name="payment_method" value="card" checked={formData.payment_method === 'card'} onChange={() => handleChange('payment_method', 'card')} className="sr-only" data-id="element-797" />
              <CreditCard className="w-5 h-5 mr-3" data-id="element-798" />
              <span className="font-semibold" data-id="element-799">Tarjeta (Datáfono)</span>
            </label>}
        </div>
      </section>

      {/* Order Notes */}
      <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-800">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-801">
          Notas Adicionales
        </h3>
        <Textarea placeholder="¿Algo más que debamos saber sobre tu pedido o entrega?" value={formData.notes} onChange={e => handleChange('notes', e.target.value)} data-id="element-802" />
      </section>

      {/* Terms & Conditions */}
      <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-803">
        <label className="flex items-start gap-3 cursor-pointer group" data-id="element-804">
          <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} className="mt-1 w-5 h-5 rounded border-gray-300 text-brand-amber focus:ring-brand-amber shrink-0" data-id="element-805" />
          <span className="text-sm text-gray-600 leading-relaxed" data-id="element-806">
            Acepto los{' '}
            <button type="button" onClick={() => setShowTermsModal(true)} className="text-brand-amber font-semibold underline underline-offset-2 hover:text-amber-600" data-id="element-807">
              Términos y Condiciones
            </button>{' '}
            y la{' '}
            <button type="button" onClick={() => setShowTermsModal(true)} className="text-brand-amber font-semibold underline underline-offset-2 hover:text-amber-600" data-id="element-808">
              Política de Privacidad
            </button>{' '}
            de NOIZA.
          </span>
        </label>
      </section>

      <Button type="submit" size="lg" fullWidth isLoading={isLoading} disabled={!termsAccepted || isLoading} className={cn('text-lg shadow-xl', (!termsAccepted || isLoading) && 'opacity-50 cursor-not-allowed')} data-id="element-809">
        {isLoading ? 'Procesando pedido...' : isPreorder ? 'Confirmar Preorden' : 'Confirmar Pedido'}
      </Button>

      {/* Terms Modal */}
      {showTermsModal && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowTermsModal(false)} data-id="element-810">
          <div className="relative w-full max-w-lg max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col" onClick={e => e.stopPropagation()} data-id="element-811">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-812">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-813">
                Términos y Condiciones
              </h2>
              <button onClick={() => setShowTermsModal(false)} className="p-2 -mr-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100" data-id="element-814">
                <AlertCircle className="w-5 h-5" data-id="element-815" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 text-sm text-gray-600 leading-relaxed space-y-4" data-id="element-816">
              <h3 className="font-bold text-gray-900" data-id="element-817">
                1. Aceptación de los Términos
              </h3>
              <p data-id="element-818">
                Al realizar un pedido a través de NOIZA, usted acepta estos
                términos y condiciones en su totalidad. Si no está de acuerdo
                con alguna parte, no debe utilizar nuestro servicio.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-819">
                2. Servicio de Pedidos
              </h3>
              <p data-id="element-820">
                NOIZA actúa como intermediario tecnológico entre el cliente y el
                restaurante. Los pedidos se procesan y confirman directamente
                con el restaurante a través de WhatsApp.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-821">3. Precios y Pagos</h3>
              <p data-id="element-822">
                Los precios mostrados incluyen impuestos. El pago se realiza
                directamente al restaurante según el método seleccionado
                (efectivo, SINPE Móvil o tarjeta). NOIZA no procesa pagos.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-823">4. Entregas</h3>
              <p data-id="element-824">
                Los tiempos de entrega son estimados y pueden variar según la
                demanda. El costo de envío se muestra antes de confirmar el
                pedido. El restaurante es responsable de la entrega.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-825">5. Cancelaciones</h3>
              <p data-id="element-826">
                Las cancelaciones deben coordinarse directamente con el
                restaurante a través de WhatsApp. Una vez que el pedido está en
                preparación, no se garantiza la cancelación.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-827">6. Datos Personales</h3>
              <p data-id="element-828">
                Recopilamos nombre, teléfono y ubicación únicamente para
                procesar su pedido. No compartimos sus datos con terceros más
                allá del restaurante seleccionado. Su información se maneja
                conforme a la Ley de Protección de Datos de Costa Rica.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-829">7. Responsabilidad</h3>
              <p data-id="element-830">
                NOIZA no es responsable por la calidad de los alimentos, tiempos
                de entrega o cualquier inconveniente con el restaurante.
                Cualquier reclamo debe dirigirse directamente al
                establecimiento.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-831">8. Modificaciones</h3>
              <p data-id="element-832">
                Nos reservamos el derecho de modificar estos términos en
                cualquier momento. Los cambios serán efectivos al publicarse en
                la plataforma.
              </p>
              <p className="text-xs text-gray-400 pt-4 border-t border-gray-100" data-id="element-833">
                Última actualización: Marzo 2026 · Los Santos, Costa Rica
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 shrink-0" data-id="element-834">
              <button onClick={() => {
            setTermsAccepted(true);
            setShowTermsModal(false);
          }} className="w-full py-3 rounded-xl bg-brand-amber text-white font-bold text-sm hover:bg-amber-600 transition-colors" data-id="element-835">
                Aceptar Términos y Condiciones
              </button>
            </div>
          </div>
        </div>}
    </form>;
}